<template>
    <div v-if="simulations">
        <top-navbar :breadcrumb="breadcrumb"></top-navbar>

        <div class="container-fluid mt--6">
            <div class="row">
                <div class="d-none d-lg-block col-lg-4">
                    
                    <user-card :user="learner"
                        :cohort="registration.cohort"
                        :groups="groups"
                        @openCohort="openCohort"
                        @openGroup="openGroup">
                    </user-card>
                    
                    <!-- Signature by learner -->
                    <signature :simulation="simulation"
                        v-if="!$permissions.hasRight('dashboard.learners.read')">
                    </signature>

                    <!-- Session info for all except learner -->
                    <session :session="session" :learner="learner"
                        v-if="session && $permissions.hasRight('dashboard.learners.read')">
                    </session>

                    <!-- Status for instructors -->
                    <status :simulation="simulation"
                        :registration="registration"
                        :learner="learner"
                        :edit-page="false"
                        v-if="$permissions.hasRight('dashboard.learners.write')"
                    >
                    </status>

                    <revisions :simulation="simulation">
                    </revisions>
                </div>
                <div class="col-lg-8">
                    <simulation
                        :simulation="simulation"
                        :simulations="simulations"
                        :registration="registration"
                        :groups="groups">
                    </simulation>

                    <div class="d-lg-none">
                        <user-card :user="learner"
                            :cohort="registration.cohort"
                            :groups="groups"
                            :compact="true"
                            @openCohort="openCohort"
                            @openGroup="openGroup">
                        </user-card>
                        
                        <!-- Signature by learner -->
                        <signature :simulation="simulation" :compact="true"
                            v-if="!$permissions.hasRight('dashboard.learners.read')">
                        </signature>

                        <!-- Session info for all except learner -->
                        <session :session="session" :learner="learner" :compact="true"
                            v-if="session && $permissions.hasRight('dashboard.learners.read')">
                        </session>

                        <!-- Status for instructors -->
                        <status :simulation="simulation"
                            :registration="registration"
                            :learner="learner"
                            :edit-page="false"
                            :compact="true"
                            v-if="$permissions.hasRight('dashboard.learners.write')"
                        >
                        </status>

                        <revisions :simulation="simulation">
                        </revisions>
                    </div>

                    <!-- Simulations -->

                    <div class="card p-3 mt-4" v-if="!simulation.assessment && !simulation.state.published">
                        <p class="text-center mb-0">{{ $t('dashboard.not-published-yet') }}</p>
                    </div>

                    <tabs class="tabs" ref="simulation-tabs"
                        tabNavClasses="nav-fill flex-column flex-sm-row nav-wrapper pb-3"
                        tabContentClasses=""
                        v-if="!simulation.assessment && simulation.state.published"
                    >

                        <tab-pane class="mb-0" id="simulation">
                            <span slot="title">
                                {{ $t('common.simulation') }}
                            </span>

                            <feedbacks
                                :registration="registration"
                                :simulation="simulation"
                                :session="session"
                                :learner="learner"
                                :competencies="competencies"
                            >
                            </feedbacks>

                            <div class="text-center mt-4 mb-4" v-if="goGround">
                                <small>
                                    <i class="fas fa-plane-departure mr-1"></i>
                                    {{ $t('degrees.go-ground-after-session') }}
                                </small>
                            </div>
                        </tab-pane>

                        <tab-pane class="card pb-1" id="grid1"
                            v-if="!groundCompetenciesDisabled(registration.degree.simulation_role_1)"
                        >
                            <span slot="title">
                                {{ $t('common.competencies') }}
                                <span v-if="registration.degree.simulation_role_2 && hasSecondGrid">
                                    : {{ registration.degree.simulation_role_1_name[registration.degree.lang] }}
                                </span>
                            </span>

                            <competencies
                                :simulations="simulations"
                                :competencies="competencies"
                                :position1-competencies="position1Competencies"
                                :position2-competencies="position2Competencies"
                                :registration="registration"
                                :rating-options="ratingOptions"
                                :num="1">
                            </competencies>

                            <div class="text-center mt-4 mb-4" v-if="goGround">
                                <small>
                                    <i class="fas fa-plane-departure mr-1"></i>
                                    {{ $t('degrees.go-ground-after-session') }}
                                </small>
                            </div>
                        </tab-pane>

                        <tab-pane class="card pb-1" id="grid2"
                            v-if="registration.degree.simulation_role_2 && hasSecondGrid && !groundCompetenciesDisabled(registration.degree.simulation_role_2)"
                        >
                            <span slot="title">
                                {{ $t('common.competencies') }}:
                                {{ registration.degree.simulation_role_2_name[registration.degree.lang] }}
                            </span>

                            <competencies
                                :simulations="simulations"
                                :competencies="competencies"
                                :position1-competencies="position1Competencies"
                                :position2-competencies="position2Competencies"
                                :registration="registration"
                                :rating-options="ratingOptions"
                                :num="2">
                            </competencies>

                            <div class="text-center mt-4 mb-4" v-if="goGround">
                                <small>
                                    <i class="fas fa-plane-departure mr-1"></i>
                                    {{ $t('degrees.go-ground-after-session') }}
                                </small>
                            </div>
                        </tab-pane>

                    </tabs>

                    <!-- Student Reviews -->
                    
                    <div class="card p-3 mt-4"
                        v-if="simulation.assessment && !simulation.state.published"
                    >
                        <p class="text-center mb-0">{{ $t('dashboard.not-published-yet') }}</p>
                    </div>

                    <div class="card p-3 mt-4"
                        v-if="simulation.assessment && simulation.state.published && !canShowReview"
                    >
                        <p class="text-center mb-0">{{ $t('dashboard.not-available-yet') }}</p>
                    </div>

                    <div class="card pb-1" id="assessment"
                        v-if="simulation.assessment && simulation.state.published && canShowReview"
                    >
                        <assessment
                            :simulation="simulation"
                            :registration="registration"
                            :rating-options="ratingOptions"
                            :domains="domains"
                        >
                        </assessment>

                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import UserCard from '../auth/components/UserCard'
    import Simulation from './learner/Simulation';
    import Competencies from './learner/SimulationCompetencies';
    import Assessment from './learner/SimulationAssessment';
    import Status from './learner/SimulationStatus';
    import Revisions from './learner/SimulationRevisions';
    import Signature from './learner/SimulationSignature';
    import Session from './learner/Session';
    import Feedbacks from '../log/common/Feedbacks';

    export default {

        components: {
            UserCard, Simulation, Competencies, Assessment, Status, Revisions, Feedbacks, Session, Signature
        },

        data() {
            return {
                learner: {},
                groups: null,
                registration: null,
                simulations: null,
                competencies: null,
                position1Competencies: null,
                position2Competencies: null,
                domains: null,
                ratingOptions: null,
                competencies: null,
            }
        },

        beforeRouteEnter(to, from, next) {
            Vue.prototype.$loader.show()
            axios.get('/astral/api/front/dashboard/registrations/'+to.params.registrationId+'/simulations/'+to.params.simulationId+'/learners/'+to.params.learnerId+'/simulations')
            .then(resp => {
                next(vm => {
                    vm.simulations = resp.data.data
                    vm.competencies = resp.data.included.competencies
                    vm.position1Competencies = resp.data.included.position1_competencies
                    vm.position2Competencies = resp.data.included.position2_competencies
                    vm.domains = resp.data.included.competency_domains
                    vm.registration = resp.data.included.registration
                    vm.learner = resp.data.included.learner
                    vm.groups = resp.data.included.groups
                    vm.ratingOptions = resp.data.included.rating_options
                    vm.competencies = resp.data.included.competencies
                    vm.$permissions.setRights(resp.data.included.rights)
                    vm.$loader.hide()
                })
            })
            .catch(err => {
                if (err.response.status == 403) {
                    next({ name: 'unauthorized' });
                }
            });
        },

        watch: {
            // Transversal learners navigation causes change of learner ID without reloading the component.
            // So we need to refresh the component manually.
            '$route.params.learnerId': function (id) {
                axios.get('/astral/api/front/dashboard/registrations/'+this.registration.id+'/simulations/'+this.$route.params.simulationId+'/learners/'+this.$route.params.learnerId+'/simulations')
                .then(resp => {
                    this.simulations = resp.data.data
                    this.competencies = resp.data.included.competencies
                    this.position1Competencies = resp.data.included.position1_competencies
                    this.position2Competencies = resp.data.included.position2_competencies
                    this.domains = resp.data.included.competency_domains
                    this.registration = resp.data.included.registration
                    this.learner = resp.data.included.learner
                    this.groups = resp.data.included.groups
                    this.ratingOptions = resp.data.included.rating_options
                    this.competencies = resp.data.included.competencies
                })
                .catch(err => {
                    this.$auth.handleErrorVm(err, this)
                })        
            }
        },

        computed: {

            canShowReview() {
                if (this.$permissions.hasRight('dashboard.overviews.always')) {
                    return true
                }
                if (this.simulation.private) {
                    return false
                }
                if (this.$permissions.hasRight('dashboard.overviews.closed')) {
                    return this.simulationClosed
                }
                if (this.$permissions.hasRight('dashboard.overviews.date-or-closed')) {
                    if (this.simulation.assessment) {
                        return this.simulationAfterDate
                    } else {
                        return this.simulationClosed
                    }
                }
                return false
            },

            simulationClosed() {
                return this.simulation.state.date
                    && new Date(this.simulation.state.date) <= new Date()
            },

            simulationAfterDate() {
                return this.simulation.state.meta
                    && this.simulation.state.meta.learner_access_date
                    && new Date(this.simulation.state.meta.learner_access_date) <= new Date()
            },

            breadcrumb() {
                let breadcrumb = []
                if (this.$auth.user.id != this.$route.params.learnerId) {
                    breadcrumb.push({
                        name: this.learner.name,
                        route: '/astral/ui/dashboard/learners/' + this.$route.params.learnerId
                    })
                }
                breadcrumb.push({
                    name: this.registration.degree.name,
                    route: '/astral/ui/dashboard/registrations/' + this.$route.params.registrationId + '/learners/' + this.$route.params.learnerId
                })
                breadcrumb.push({
                    name: this.simulation.name,
                })
                return breadcrumb
            },

            simulation() {
                let index = this.simulations.findIndex(simulation => simulation.id == this.$route.params.simulationId)
                return this.simulations[index]
            },

            session() {
                return this.simulation.session ? this.simulation.session : null
            },

            goGround() {
                return this.registration.state.meta
                    ? this.registration.state.meta.go_ground == this.$route.params.simulationId
                    : false
            },

            hasSecondGrid() {
                for (var index in this.simulations) {
                    if (this.simulations[index].grid2_id) {
                        return true
                    }
                }
                return false
            },
        },

        methods: {

            groundCompetenciesDisabled(simulationRole) {
                if (simulationRole !== 'sol'
                    || !this.registration.degree.go_ground
                    || !this.registration.state.meta
                    || !this.registration.state.meta.go_ground
                ) {
                    return false
                }
                let goGround = false
                let currentSimFound = false
                this.simulations.forEach(simulation => {
                    if (simulation.id == this.simulation.id) {
                        currentSimFound = true
                    }
                    if (currentSimFound) {
                        // Don't continue after the current simulation. No break with ES6.
                        return
                    }
                    if (this.registration.state.meta.go_ground == simulation.id) {
                        goGround = true
                    }
                })
                return goGround
            },

            openCohort() {
                this.$router.push({
                    name: 'dashboard-cohort-simulation',
                    params: {
                        cohortId: this.registration.cohort.id,
                        registrationId: this.$route.params.registrationId,
                        simulationId: this.$route.params.simulationId
                    }
                });
            },

            openGroup() {
                this.$router.push({
                    name: 'dashboard-group-simulation',
                    params: {
                        groupId: this.groups[0].id,
                        registrationId: this.$route.params.registrationId,
                        simulationId: this.$route.params.simulationId
                    }
                });
            },
        }
    }
</script>

<style scoped>

    #assessment {
        box-shadow: 0 0 2rem 0 rgba(136,152,170,.3);
        margin-top: 2rem;
    }
    .tabs >>> .nav-pills {
        padding-top: 0;
        padding-bottom: 1.5rem;
    }

</style>
